/* AWA Vision sibling cards v1.2.0. No stored pricing or variant-ID mutation. */
(() => {
  'use strict';
  if (window.awaVisionSiblingsLoaded) return;
  window.awaVisionSiblingsLoaded = true;
  const requests = new WeakMap();
  const states = new WeakMap();

  function markBusy(card) {
    if (!states.has(card)) {
      states.set(card, [...card.querySelectorAll('button')].map(button => [button, button.disabled]));
    }
    card.setAttribute('data-awa-switching', '');
    card.setAttribute('aria-busy', 'true');
    card.querySelectorAll('button').forEach(button => { button.disabled = true; });
  }
  function restore(card) {
    card.removeAttribute('data-awa-switching');
    card.removeAttribute('aria-busy');
    (states.get(card) || []).forEach(([button, disabled]) => { button.disabled = disabled; });
    states.delete(card);
  }
  function previewURL(href) {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Invalid product URL');
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    url.searchParams.set('view', 'awa-card');
    return url;
  }
  function retainSeriesChoices(previous, next) {
    if (next.querySelector('[data-awa-siblings]')) return;
    const original = previous.querySelector('[data-awa-siblings]');
    const info = next.querySelector('.product-card-info');
    if (!original || !info) return;
    // Keep the already-rendered group when the linked product has no reciprocal list.
    // Prices, identity, product links and purchase controls still come from the new server card.
    const group = original.cloneNode(true);
    let selected = null;
    group.querySelectorAll('[data-awa-sibling-link]').forEach(choice => {
      const active = choice.dataset.handle === next.dataset.awaHandle;
      choice.classList.toggle('is-selected', active);
      choice.removeAttribute('aria-current');
      if (active) { choice.setAttribute('aria-current', 'true'); selected = choice; }
    });
    if (!selected) return;
    const available = next.dataset.awaAvailable;
    if (available === 'true' || available === 'false') {
      selected.classList.toggle('is-unavailable', available === 'false');
      selected.querySelector('.awa-vision-sr')?.remove();
      if (available === 'false') {
        const note = document.createElement('span');
        note.className = 'awa-vision-sr'; note.textContent = ' — Sold out'; selected.append(note);
      }
      const title = next.querySelector('.product-card-title')?.textContent?.trim();
      if (title) selected.title = title + (available === 'false' ? ' — Sold out' : '');
      const stock = group.querySelector('.awa-card-siblings__stock');
      if (stock) {
        stock.classList.toggle('is-unavailable', available === 'false');
        const dot = document.createElement('span'); dot.setAttribute('aria-hidden', 'true');
        stock.replaceChildren(dot, document.createTextNode(available === 'true' ? 'Available to order' : 'Sold out'));
      }
    } else {
      // Never reuse the old model's stock label when the response provides no new stock state.
      group.querySelector('.awa-card-siblings__stock')?.remove();
    }
    const feedback = group.querySelector('[data-awa-sibling-feedback]');
    if (feedback) feedback.textContent = '';
    const purchase = info.querySelector('.product-card--add-to-cart-button-wrapper');
    info.insertBefore(group, purchase || null);
    next.classList.add('awa-vision-sibling-card');
  }
  async function changeModel(card, link) {
    requests.get(card)?.abort();
    const controller = new AbortController();
    requests.set(card, controller);
    const timer = setTimeout(() => controller.abort(), 15000);
    const feedback = card.querySelector('[data-awa-sibling-feedback]');
    markBusy(card);
    if (feedback) feedback.textContent = `Loading ${link.dataset.label || 'model'}…`;
    try {
      const response = await fetch(previewURL(link.href), {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error('Product could not be loaded');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('[data-awa-card-response] product-card');
      if (!next || next.dataset.awaHandle !== link.dataset.handle) throw new Error('No matching product card');
      if ((next.dataset.awaBuyer || '') !== (card.dataset.awaBuyer || '')) throw new Error('Buyer context changed');
      if (requests.get(card) !== controller || !card.isConnected) return;
      retainSeriesChoices(card, next);
      // Import only the server-rendered card. Never execute scripts from the fetched page.
      next.querySelectorAll('script').forEach(script => script.remove());
      if (card.classList.contains('awa-vision-location-card')) next.classList.add('awa-vision-location-card');
      if (card.dataset.awaQuickAdd === 'false') { next.querySelector('.product-card--add-to-cart-button-wrapper')?.remove(); next.dataset.awaQuickAdd = 'false'; }
      const replacement = document.adoptNode(next);
      card.replaceWith(replacement);
      const selected = replacement.querySelector('[data-awa-sibling-link][aria-current]');
      selected?.focus({ preventScroll: true });
      const newFeedback = replacement.querySelector('[data-awa-sibling-feedback]');
      if (newFeedback) newFeedback.textContent = `${link.dataset.label || 'Model'} selected.`;
      if (window.lazySizes?.loader) replacement.querySelectorAll('img.lazyload').forEach(image => window.lazySizes.loader.unveil(image));
      // Refresh a native Vision carousel after a card-sized cell changes.
      const carousel = replacement.closest('.flickity-enabled');
      if (carousel && window.Flickity) {
        const flickity = window.Flickity.data(carousel);
        flickity?.reloadCells(); flickity?.resize();
      }
      document.dispatchEvent(new CustomEvent('awa:sibling-change', {
        detail: { handle: replacement.dataset.awaHandle, productId: replacement.dataset.awaProductId, card: replacement }
      }));
      states.delete(card);
    } catch (error) {
      if (requests.get(card) !== controller || !card.isConnected) return;
      restore(card);
      if (feedback) {
        feedback.textContent = 'Could not switch this card. ';
        const fallback = document.createElement('a');
        fallback.href = link.href;
        fallback.textContent = 'Open this model';
        feedback.append(fallback);
      }
    } finally {
      clearTimeout(timer);
      if (requests.get(card) === controller) requests.delete(card);
    }
  }
  // Capture protects the old model's purchase action while a replacement is loading.
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const card = event.target.closest('product-card');
    if (!card) return;
    const link = event.target.closest('[data-awa-sibling-link]');
    if (!link) {
      if (card.hasAttribute('data-awa-switching') && event.target.closest('a,button,quick-view')) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
      return;
    }
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    if (link.closest('[data-awa-siblings]')?.dataset.swap !== 'true') return;
    event.preventDefault(); event.stopPropagation();
    if (link.hasAttribute('aria-current') && !card.hasAttribute('data-awa-switching')) return;
    if (card.querySelector('.product-card--add-to-cart-button.loading, quick-view.loading')) return;
    changeModel(card, link);
  }, true);
})();
